import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import io from 'socket.io-client';
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import litecoinLogo from '../images/litecoin-logo.png';
import paypalLogo from '../images/PayPal-Logo-PNG-Free-Image-3034339437.png';
import NotFound from './NotFound';
import OutOfStock from './OutOfStock';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const ProductPage = () => {
  const { id, customUrl } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [email, setEmail] = useState('');
  const [contact, setContact] = useState('');
  const [referral, setReferral] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [checkoutStep, setCheckoutStep] = useState('details');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [isOutOfStock, setIsOutOfStock] = useState(false);

  // Normaliser le chemin de l'image
  const normalizedImagePath = product?.image && !product.image.startsWith('/') ? `/${product.image}` : product?.image;

  const fetchProduct = useCallback(async () => {
    try {
      console.log('Fetching product with ID:', id, 'or customUrl:', customUrl);
      
      // Fetch both autobuy and catalog products
      const [autobuyResponse, catalogResponse] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/products`),
        fetch(`${process.env.REACT_APP_API_URL}/api/products/catalog`)
      ]);
      
      if (!autobuyResponse.ok || !catalogResponse.ok) {
        console.error('Server response not OK:', autobuyResponse.status, catalogResponse.status);
        setNotFound(true);
        return;
      }
      
      const [autobuyProducts, catalogProducts] = await Promise.all([
        autobuyResponse.json(),
        catalogResponse.json()
      ]);
      
      // Combine both product types
      const allProducts = [...autobuyProducts, ...catalogProducts];
      console.log('Received products:', allProducts);
      
      // Si nous avons un ID de produit
      if (id) {
        const productById = allProducts.find(p => p.id === id);
        // Si le produit a une URL personnalisée, on renvoie 404
        if (productById && productById.customUrl) {
          setNotFound(true);
          return;
        }
        // Si le produit existe et n'a pas d'URL personnalisée, on le renvoie
        if (productById) {
          setProduct(productById);
          // Vérifier si le produit est en rupture de stock
          if (productById.stock === 0) {
            setIsOutOfStock(true);
          }
          if (productById.deliveryType === 'explicit_api' && productById.minQuantity) {
            setQuantity(productById.minQuantity);
          }
          return;
        }
      }
      
      // Si nous avons une URL personnalisée
      if (customUrl) {
        const productByCustomUrl = allProducts.find(p => p.customUrl === customUrl);
        if (productByCustomUrl) {
          setProduct(productByCustomUrl);
          // Vérifier si le produit est en rupture de stock
          if (productByCustomUrl.stock === 0) {
            setIsOutOfStock(true);
          }
          if (productByCustomUrl.deliveryType === 'explicit_api' && productByCustomUrl.minQuantity) {
            setQuantity(productByCustomUrl.minQuantity);
          }
          return;
        }
      }
      
      // Si aucun produit n'est trouvé
      setNotFound(true);
    } catch (error) {
      console.error('Error fetching product:', error);
      setNotFound(true);
    }
  }, [id, customUrl]);

  const paymentMethods = [
    {
      id: 'litecoin',
      name: 'Litecoin (LTC)',
      icon: <img src={litecoinLogo} alt="Litecoin" className="w-8 h-8" />,
      description: 'Pay with Litecoin - Fast & Low Fees'
    },
    {
      id: 'paypal',
      name: 'PayPal',
      icon: <div className="w-8 h-8 flex items-center justify-center">
        <img src={paypalLogo} alt="PayPal" className="w-24 h-6 object-contain" />
      </div>,
      description: 'Pay with PayPal - Only available on Discord',
    }
  ];

  const calculateTotal = (basePrice, quantity, paymentMethod) => {
    const discountedPrice = product.discount > 0 
      ? parseFloat(basePrice) * (1 - product.discount / 100)
      : parseFloat(basePrice);
    const subtotal = discountedPrice * quantity;
    return subtotal;
  };

  useEffect(() => {
    fetchProduct();
    
    const newSocket = io(process.env.REACT_APP_API_URL);

    newSocket.on('connect', () => {
      console.log('Socket.IO connected successfully');
    });

    newSocket.on('product_updated', (updatedProduct) => {
      if (updatedProduct.id === id || updatedProduct.id === product?.id) {
        setProduct(updatedProduct);
        // Vérifier si le produit est en rupture de stock
        const inStock = updatedProduct.deliveryType === 'explicit_api' || 
          (Array.isArray(updatedProduct.stock) && updatedProduct.stock.length > 0);
        if (!inStock) {
          setIsOutOfStock(true);
        }
      }
    });

    newSocket.on('new_feedback', (feedback) => {
      if (feedback.productId === id) {
        setProduct(prev => ({
          ...prev,
          feedbacks: [...(prev.feedbacks || []), feedback]
        }));
      }
    });

    newSocket.on('feedback_updated', (updatedFeedback) => {
      if (updatedFeedback.productId === id) {
        setProduct(prev => ({
          ...prev,
          feedbacks: prev.feedbacks.map(f => 
            f.id === updatedFeedback.id ? updatedFeedback : f
          )
        }));
      }
    });

    newSocket.on('product_deleted', (deletedProductId) => {
      if (deletedProductId === id) {
        navigate('/');
      }
    });

    return () => newSocket.disconnect();
  }, [id, navigate, fetchProduct, product?.id]);

  const handleContinueToPayment = (e) => {
    e.preventDefault();
    if (!email) {
      alert('Please enter your email address');
      return;
    }
    setCheckoutStep('payment');
  };

  const handleSelectPayment = (methodId) => {
    const method = paymentMethods.find(m => m.id === methodId);
    if (method && !method.disabled) {
      setSelectedPaymentMethod(methodId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPaymentMethod || !termsAccepted) return;
    setError(''); // Reset error message
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/create-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product,
          quantity,
          email,
          contact,
          referral,
          paymentMethod: selectedPaymentMethod
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        if (data.invoiceId) {
          navigate(`/payment/${data.invoiceId}`);
        }
      } else {
        if (data.error === 'Insufficient stock') {
          if (data.availableStock === 0) {
            setIsOutOfStock(true);
          } else {
            setError(`Sorry, only ${data.availableStock} items are currently available.`);
            setQuantity(Math.min(quantity, data.availableStock));
          }
        } else {
          setError(data.error || 'Failed to create invoice. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      setError('Failed to create invoice. Please try again.');
    }
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
    
    // Permettre un champ vide ou une valeur numérique
    if (value === '' || value === null) {
      setQuantity('');
      return;
    }

    // Autoriser uniquement les nombres
    if (/^\d*$/.test(value)) {
      setQuantity(value);
    }
  };

  const handleQuantityBlur = () => {
    const numValue = parseInt(quantity);
    const maxAllowed = product.deliveryType === 'explicit_api' ? product.maxQuantity : (product.stock || []).length;
    const minAllowed = product.deliveryType === 'explicit_api' ? product.minQuantity : 1;

    // Si vide ou invalide, mettre la valeur minimale
    if (!quantity || isNaN(numValue)) {
      setQuantity(minAllowed);
      return;
    }

    // Ajuster la valeur si elle est hors limites
    if (numValue < minAllowed) {
      setQuantity(minAllowed);
    } else if (numValue > maxAllowed) {
      setQuantity(maxAllowed);
    } else {
      setQuantity(numValue);
    }
  };

  const renderCheckoutForm = () => {
    if (checkoutStep === 'details') {
      return (
        <form onSubmit={handleContinueToPayment} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Quantity (Min: {product.deliveryType === 'explicit_api' ? product.minQuantity : 1}, Max: {product.deliveryType === 'explicit_api' ? product.maxQuantity : (product.stock || []).length})
              <div className="flex items-center mt-1">
                <button
                  type="button"
                  onClick={() => {
                    const currentValue = parseInt(quantity) || 0;
                    const minAllowed = product.deliveryType === 'explicit_api' ? product.minQuantity : 1;
                    if (currentValue > minAllowed) {
                      setQuantity(currentValue - 1);
                    }
                  }}
                  className="bg-[#141414] text-white px-3 py-2 rounded-l"
                >
                  -
                </button>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  value={quantity}
                  onChange={handleQuantityChange}
                  onBlur={handleQuantityBlur}
                  className="bg-[#1f1f1f] text-white text-center w-20 py-2"
                />
                <button
                  type="button"
                  onClick={() => {
                    const currentValue = parseInt(quantity) || 0;
                    const maxAllowed = product.deliveryType === 'explicit_api' ? product.maxQuantity : (product.stock || []).length;
                    if (currentValue < maxAllowed) {
                      setQuantity(currentValue + 1);
                    }
                  }}
                  className="bg-[#141414] text-white px-3 py-2 rounded-r"
                >
                  +
                </button>
              </div>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              Email Address *
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full bg-[#141414] border border-gray-600 rounded-md shadow-sm py-2 px-3"
                placeholder="Enter your email address"
                autoComplete="email"
                name="email"
              />
            </label>
          </div>

          {product.requiresGuildId && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Discord Server ID *
                <div className="relative">
                  <input
                    type="text"
                    required
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    pattern="^\d{17,19}$"
                    className="mt-1 block w-full bg-[#141414] border border-gray-600 rounded-md shadow-sm py-2 px-3"
                    placeholder="Enter your Discord Server ID"
                  />
                  <div className="absolute right-0 top-0 mt-1 mr-2">
                    <button
                      type="button"
                      onClick={() => window.open('https://support.discord.com/hc/en-us/articles/206346498-Where-can-I-find-my-User-Server-Message-ID-', '_blank')}
                      className="text-sm text-[#22c55e] hover:text-[#15803d]"
                    >
                      How to get ID?
                    </button>
                  </div>
                </div>
                <p className="mt-1 text-sm text-gray-400">
                  Right-click on your server name and select "Copy Server ID"
                </p>
              </label>
            </div>
          )}

          {!product.requiresGuildId && (
            <div>
              <label className="block text-sm font-medium mb-2">
                Discord / Telegram Contact (Optional)
                <input
                  type="text"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  className="mt-1 block w-full bg-[#141414] border border-gray-600 rounded-md shadow-sm py-2 px-3"
                  placeholder="Enter your Discord or Telegram username (@https://t.me/olympstorecc ou DiscordTag)"
                />
              </label>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">
              How did you find us? (Optional)
              <input
                type="text"
                value={referral}
                onChange={(e) => setReferral(e.target.value)}
                className="mt-1 block w-full bg-[#141414] border border-gray-600 rounded-md shadow-sm py-2 px-3"
                placeholder="Tell us how you found our service"
              />
            </label>
          </div>

          <div className="border-t border-gray-700 pt-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Total</span>
              <span className="text-[#22c55e] text-2xl font-bold">{product.productType === 'catalog' ? `€${calculateTotal(product.price, quantity, 'litecoin').toFixed(2)}` : `$${calculateTotal(product.price, quantity, 'litecoin').toFixed(2)}`}</span>
            </div>
          </div>

          <button
            type="submit"
            className="neon-button w-full mt-4"
          >
            Continue to Payment
          </button>
        </form>
      );
    }

    if (checkoutStep === 'payment') {
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-medium mb-4">Select Payment Method</h3>
          <div className="grid gap-4">
            {paymentMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => method.onClick ? method.onClick() : handleSelectPayment(method.id)}
                className={`relative flex items-center p-4 rounded-lg border-2 cursor-pointer
                  ${method.disabled 
                    ? 'border-gray-700 opacity-50 cursor-not-allowed' 
                    : selectedPaymentMethod === method.id
                      ? 'border-[#22c55e] bg-[#22c55e]/10'
                      : 'border-gray-700 hover:border-gray-600'}`}
              >
                <div className="flex items-center flex-1">
                  <span className={`mr-3 flex items-center justify-center ${!method.disabled && method.id === 'litecoin' ? 'text-[#22c55e]' : ''}`}>{method.icon}</span>
                  <div className="flex-1">
                    <p className={`text-sm font-medium text-white ${!method.disabled && method.id === 'litecoin' ? 'text-[#22c55e]' : ''}`}>{method.name}</p>
                    <p className="text-sm text-gray-400">{method.description}</p>
                    {method.note && (
                      <p className="text-xs text-gray-400 mt-1">{method.note}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="border-t border-gray-700 pt-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Total</span>
              <span className="text-[#22c55e] text-2xl font-bold">{product.productType === 'catalog' ? `€${calculateTotal(product.price, quantity, 'litecoin').toFixed(2)}` : `$${calculateTotal(product.price, quantity, 'litecoin').toFixed(2)}`}</span>
            </div>
          </div>

          <button
            onClick={() => {
                              if (selectedPaymentMethod === 'paypal') {
                window.location.href = 'https://discord.gg/olymp4';
              } else if (selectedPaymentMethod) {
                setCheckoutStep('checkout');
              }
            }}
            disabled={!selectedPaymentMethod}
            className={`neon-button w-full mb-4 ${!selectedPaymentMethod ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Next
          </button>

          <button
            onClick={() => setCheckoutStep('details')}
            className="neon-button w-full"
          >
            Back
          </button>
        </div>
      );
    }

    if (checkoutStep === 'checkout') {
      return (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Total</span>
              <span className="text-[#22c55e] text-2xl font-bold">{product.productType === 'catalog' ? `€${calculateTotal(product.price, quantity, 'litecoin').toFixed(2)}` : `$${calculateTotal(product.price, quantity, 'litecoin').toFixed(2)}`}</span>
            </div>

            <div className="bg-[#141414] rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Product</span>
                <span className="text-white">{product.title}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Quantity</span>
                <span className="text-white">{quantity}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Email</span>
                <span className="text-white">{email}</span>
              </div>
              {contact && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Contact</span>
                  <span className="text-white">{contact}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Payment Method</span>
                <span className="text-white">Litecoin (LTC)</span>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="terms"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="rounded border-gray-600 bg-[#141414] text-[#22c55e] focus:ring-[#22c55e]"
              />
              <label htmlFor="terms" className="text-sm text-gray-300">
                I agree to the <Link to="/terms" className="text-[#22c55e] hover:text-[#15803d]">Terms of Service</Link>
              </label>
            </div>

            <button
              type="submit"
              disabled={!termsAccepted}
              className={`neon-button w-full mb-4 ${!termsAccepted ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Next
            </button>

            <button
              type="button"
              onClick={() => setCheckoutStep('payment')}
              className="neon-button w-full"
            >
              Back
            </button>
          </div>
        </form>
      );
    }
  };

  // Si la page n'est pas trouvée, afficher le composant NotFound
  if (notFound) {
    return <NotFound />;
  }

  if (isOutOfStock) {
    return <OutOfStock />;
  }

  // Si le produit n'est pas trouvé, retourner null
  if (!product) {
    return null;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="glass-card p-8 max-w-md w-full text-center">
          <div className="text-[#22c55e] mb-4 text-2xl">⚠️</div>
          <div className="text-xl font-bold mb-4">Oops! Something went wrong</div>
          <div className="text-gray-400 mb-6">{error}</div>
          <div className="flex flex-col gap-4">
            <button
              onClick={() => window.location.reload()}
              className="neon-button w-full"
            >
              Refresh Page
            </button>
            <Link to="/" className="text-[#22c55e] hover:text-[#15803d] transition-colors">
              Return to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <Link 
        to="/" 
        className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition-colors duration-200 mb-4"
      >
        <ArrowLeftIcon className="w-5 h-5" />
        <span>Back to Store</span>
      </Link>

      {product.productType === 'catalog' ? (
        <>
          <div className="flex flex-col md:flex-row gap-8">
            {/* Left Column - Product Information */}
            <div className="flex-1">
              <div className="glass-card p-8 rounded-lg bg-[#0f0f0f]">
                <h2 className="text-2xl font-bold text-white mb-6">Product Information</h2>
                
                <div className="border-b border-gray-800 mb-8"></div>

                <div className="mb-8">
                  <div className="space-y-3 text-gray-300">
                    {product.description.split('\n').map((line, index) => {
                      // Handle headers with formatting
                      if (line.startsWith('# ') || line.startsWith('## ')) {
                        const isMainHeader = line.startsWith('# ');
                        const headerContent = line.slice(isMainHeader ? 2 : 3);
                        
                        // Process formatting within header
                        const formattedContent = headerContent.split(/(\*\*.*?\*\*|__.*?__)/g).map((part, partIndex) => {
                          if (part.startsWith('**') && part.endsWith('**')) {
                            return <strong key={partIndex}>{part.slice(2, -2)}</strong>;
                          } else if (part.startsWith('__') && part.endsWith('__')) {
                            return <u key={partIndex}>{part.slice(2, -2)}</u>;
                          }
                          return part;
                        });

                        return isMainHeader ? (
                          <h2 key={index} className="text-2xl font-bold text-white mb-4">{formattedContent}</h2>
                        ) : (
                          <h3 key={index} className="text-xl font-semibold text-white mb-3">{formattedContent}</h3>
                        );
                      }
                      
                      // Handle bullet points and text
                      if (line.trim()) {
                        const isBulletPoint = line.trim().startsWith('-');
                        const textContent = isBulletPoint ? line.trim().slice(1).trim() : line.trim();
                        
                        return (
                          <div key={index} className={`${isBulletPoint ? 'flex items-start' : ''}`}>
                            {isBulletPoint && <span className="text-[#22c55e] mr-2">•</span>}
                            <div>
                              {textContent.split(/(\*\*.*?\*\*|__.*?__)/g).map((part, partIndex) => {
                                if (part.startsWith('**') && part.endsWith('**')) {
                                  return <strong key={partIndex}>{part.slice(2, -2)}</strong>;
                                } else if (part.startsWith('__') && part.endsWith('__')) {
                                  return <u key={partIndex}>{part.slice(2, -2)}</u>;
                                }
                                return part;
                              })}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Image and Purchase */}
            <div className="md:w-[500px]">
              <div className="glass-card p-6 rounded-lg bg-[#0f0f0f] sticky top-4">
                <img
                  src={normalizedImagePath}
                  alt={product.title}
                  className="w-full rounded-lg mb-6 object-cover"
                  style={{ maxHeight: '300px' }}
                />
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold text-white">{product.title}</h3>
                  
                  <div className="bg-[#141414] rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <div className="text-gray-400">Price</div>
                      <div className="text-gray-400">Stock</div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-[#22c55e] text-2xl font-bold">{product.productType === 'catalog' ? `€${product.price}` : `$${product.price}`}</div>
                      <div className="text-2xl">∞</div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 text-gray-400 text-sm">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                    </svg>
                    <span>Purchases are via discord</span>
                  </div>

                  <button
                    onClick={() => {
                      window.location.href = 'https://discord.gg/olymp4';
                    }}
                    className="neon-button w-full py-4 text-lg font-semibold"
                  >
                    Purchase
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Product Info */}
          <div className="mt-8">
            <h1 className="text-4xl font-bold text-white mb-6">{product.title}</h1>
            {product.onHold && (
              <div className="bg-yellow-500/10 text-yellow-500 p-4 rounded-lg mb-6">
                This product is temporarily unavailable for purchase.
              </div>
            )}
            <img
              src={normalizedImagePath}
              alt={product.title}
              className="product-image w-full mb-6"
            />
            <div className="glass-card p-6 rounded-lg bg-[#0f0f0f]">
              {product.productType === 'catalog' ? (
                <>
                  <h2 className="text-2xl font-bold mb-4">Description</h2>
                  <div className="text-gray-300 whitespace-pre-wrap">
                    {product.description.split(/(\*\*.*?\*\*|__.*?__)/g).map((part, index) => {
                      if (part.startsWith('**') && part.endsWith('**')) {
                        return <strong key={index}>{part.slice(2, -2)}</strong>;
                      } else if (part.startsWith('__') && part.endsWith('__')) {
                        return <u key={index}>{part.slice(2, -2)}</u>;
                      }
                      return part;
                    })}
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-bold mb-4">Features</h2>
                  <div className="flex flex-col space-y-4">
                    {product.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <svg className="w-5 h-5 text-[#22c55e]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Customer Reviews */}
            <div className="glass-card p-6 mt-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Customer Reviews</h2>
                <Link to="/reviews" className="link-text text-lg">
                  View All Reviews
                </Link>
              </div>
              <div className="space-y-4">
                {product.feedbacks && product.feedbacks.length > 0 ? (
                  [...product.feedbacks]
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                    .slice(0, 3)
                    .map((feedback, index) => (
                    <div key={index} className="glass-card p-4">
                      <div className="flex items-center space-x-2">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((star) => (
                            star <= feedback.rating ? (
                              <StarIconSolid key={star} className="h-5 w-5 review-star filled" />
                            ) : (
                              <StarIconOutline key={star} className="h-5 w-5 review-star" />
                            )
                          ))}
                        </div>
                        <span className="text-sm text-gray-400">
                          {new Date(feedback.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="mt-2 text-gray-300">{feedback.message}</p>
                      {feedback.reply && (
                        <div className="mt-2 pl-4 border-l-2 border-primary">
                          <p className="text-sm text-gray-400">Admin Reply:</p>
                          <p className="text-sm text-gray-300">{feedback.reply}</p>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No reviews yet</p>
                    <p className="text-gray-500 mt-2">Be the first to leave a review!</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Checkout Form */}
          <div className="glass-card p-6 rounded-lg mt-8 h-fit">
            {product.productType === 'catalog' ? (
              <>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-2xl font-bold text-[#22c55e]">{product.productType === 'catalog' ? `€${product.price}` : `$${product.price}`}</span>
                      <div className="text-gray-400 text-sm mt-1">
                        <span className="flex items-center">
                          Quantity: <span className="ml-1 text-lg">∞</span>
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        window.location.href = 'https://discord.gg/olymp4';
                      }}
                      className="neon-button px-8 py-3"
                    >
                      Purchase
                    </button>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-400 text-sm border-t border-gray-800 pt-4">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                    </svg>
                    <span>Purchases are processed via Discord</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold mb-6">Checkout Details</h2>
                {error && (
                  <div className="mb-6 bg-red-500/20 text-red-500 p-4 rounded-lg">
                    {error}
                  </div>
                )}
                {product.onHold ? (
                  <div className="text-center py-8">
                    <div className="bg-yellow-500/10 text-yellow-500 p-4 rounded-lg mb-4">
                      This product is temporarily unavailable for purchase.
                      Please check back later or contact support for more information.
                    </div>
                  </div>
                ) : (
                  renderCheckoutForm()
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPage; 